import type { Counterparty, CounterpartyMapWithLinks } from '@gcv/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { BanksApi } from 'api';
import { BANK_COUNTERPARTIES_KEY } from 'tanstack-hooks/keys';
import { useUser } from 'tanstack-hooks/user';

export function useUpdateCounterparty() {
  const { userBankId } = useUser();
  const banksApi = new BanksApi();
  const queryClient = useQueryClient();

  const { mutateAsync: updateCounterparty, isPending: isUpdatingCounterparty } = useMutation({
    mutationKey: [BANK_COUNTERPARTIES_KEY],
    mutationFn: async (counterparties: Counterparty[]) => {
      return await banksApi.upsertBankCounterparties(userBankId, counterparties);
    },
    onSuccess: (updatedCounterparties) => {
      // Optimistically update cache instead of invalidating to avoid refetch flicker
      queryClient.setQueriesData(
        { queryKey: [BANK_COUNTERPARTIES_KEY] },
        (old: CounterpartyMapWithLinks | undefined) => {
          if (!old) return old;
          const updated = { ...old };
          for (const counterparty of updatedCounterparties) {
            if (updated[counterparty.id]) {
              updated[counterparty.id] = { ...updated[counterparty.id], counterparty };
            } else {
              updated[counterparty.id] = { counterparty, link: null };
            }
          }
          return updated;
        }
      );
    },
    meta: {
      snackbarSuccessMessage: 'Counterparty updated successfully.',
      snackbarErrorMessage:
        'There was an issue updating the counterparty. Please try again, or contact support if the problem persists.'
    }
  });

  return { updateCounterparty, isUpdatingCounterparty };
}
